import { useEffect, useRef, useState, type UIEvent } from 'react';

import { toast } from 'sonner';

import { FactLocationV2 } from '../../../../../backend/src/matter/fact/fact-location.entity';
import { SubsetFact } from '../../../../../backend/src/types/types';
import { Button } from '../shadcn/button';
import { FactDetails, FactDetailsLocation } from '@/api/types/backend-dto-types';
import { Chip } from '@/new/components/chip';
import { SourceConfidenceWarning } from '@/new/components/fact/source-confidence-warning';
import { Icon } from '@/new/components/icon';
import { CHIP_COLOURS } from '@/new/lib/chip-colours';
import { ChronologyFact } from '@/types';

type FactType = SubsetFact | ChronologyFact | FactDetails;

interface Props {
  fact: FactType;
  fullDocRow?: boolean;
  gotoDocReference: (docId: string, factId: string, pageNumber: number) => void;
}

const isChronologyFact = (fact: FactType): fact is ChronologyFact => {
  return 'fact' in fact && 'chronologyId' in fact;
};

export const FactDocumentDetails = ({ fact, fullDocRow = true, gotoDocReference }: Props) => {
  const uploadDate = new Date(fact.createdDate);

  // Handle SubsetFact, ChronologyFact, and FactDetailsResponseDto
  const locations = isChronologyFact(fact) ? fact.fact.locations : fact.locations;
  const factId = isChronologyFact(fact) ? fact.fact.id : fact.id;

  return (
    <div className="flex flex-col gap-2">
      <p className="font-medium">Sources</p>

      {locations.map((factLocation: FactLocationV2 | FactDetailsLocation, index: number) => {
        const pageNumber = factLocation?.boundingBoxes?.length > 0 ? factLocation?.boundingBoxes?.[0]?.pageNumber : 1;

        return (
          <div key={`${factLocation.docId}-${index}`} className="flex w-full flex-col gap-2 not-last:mb-2">
            <div className="flex w-full items-baseline gap-2">
              <div className="flex items-center gap-2">
                <p
                  onClick={() => gotoDocReference(factLocation.docId, factId, pageNumber)}
                  className="flex cursor-pointer items-center gap-1.25 text-xs break-all text-ellipsis text-blue-600 duration-150 hover:text-blue-700"
                >
                  <Icon className="lucide--file mb-0.25" />
                  <span className="line-clamp-1">{factLocation.doc?.file.name}</span>
                </p>

                <Chip className={CHIP_COLOURS.type}>{factLocation.doc?.type}</Chip>
              </div>

              {fullDocRow && (
                <div className="ml-auto text-xs whitespace-nowrap text-gray-500">
                  Uploaded{' '}
                  <span className="font-semibold">{`${uploadDate.getDate()}/${uploadDate.getMonth() + 1}/${uploadDate.getFullYear()}`}</span>
                </div>
              )}
            </div>

            <div className="border-l-2 border-gray-200 pl-4 text-gray-600">
              {factLocation.sources &&
                factLocation?.sources?.map((f, i) => {
                  return (
                    <div key={i} className="py-1 text-xs">
                      <div className="mb-0.5 flex items-center justify-between gap-2">
                        <div>
                          <span>
                            [Page{f.pages.includes(',') ? 's' : ''} {f.pages}]
                          </span>
                          <SourceConfidenceWarning sourceConfidence={f.sourceConfidence} />
                        </div>

                        <Button
                          size="sm"
                          className="mb-1.5 gap-1 p-0.5 px-1.25 pl-1.5 text-[11px]"
                          onClick={() => {
                            navigator.clipboard.writeText(f.text);
                            toast.success('Source text copied to clipboard');
                          }}
                        >
                          <Icon className="lucide--copy text-[10px]" />
                          Copy source text
                        </Button>
                      </div>
                      <SourceText text={f.text ? f.text : ''} />
                    </div>
                  );
                })}
            </div>
          </div>
        );
      })}
    </div>
  );
};

const SourceText = ({ text }: { text: string }) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [isScrolledToBottom, setIsScrolledToBottom] = useState(false);

  useEffect(() => {
    const target = scrollContainerRef.current;

    if (target) {
      setIsScrolledToBottom(target.scrollHeight - target.scrollTop <= target.clientHeight + 1);
    }
  }, [text]);

  const handleScroll = (e: UIEvent<HTMLDivElement>) => {
    const target = e.currentTarget;

    setIsScrolledToBottom(target.scrollHeight - target.scrollTop <= target.clientHeight + 1);
  };

  return (
    <div
      ref={scrollContainerRef}
      onScroll={handleScroll}
      className="max-h-16 overflow-y-auto mask-b-from-70% mask-b-to-105% transition-[mask-size]"
      style={{ maskSize: isScrolledToBottom ? '100% 130%' : '100% 100%' }}
    >
      {text}
    </div>
  );
};
